import React, { useEffect, useState } from 'react';
import Styles from './PropertyField.module.css';

import { Dropdown, FormLabel, ProviderConsumer } from '@fluentui/react-northstar';
import { useTranslation } from 'react-i18next';
import { AUTOMATION_PROPERTIES, CustomPropertiesAnswerJsonKey } from '../../utils/constants';
import { getTranslateAutomationProperties } from '../../../automation/utils/automationHelper';
import { findAlreadyAddedProperty } from '../../../automation/utils/doPropertyAvailability';
import { useDropdownSearchQuery } from '../../hooks/useDropdownSearchQuery';
import { readSelection } from '../../utils/dropdownSelection';

interface Props {
  propertiesList: DropdownDataModel[];
  property: string;
  updateProperty: (property: string) => void;
  whenData?: AutomationWhenData;
  alreadyAddedProperties?: DropdownDataModel[];
}

export const PropertyField = ({
  propertiesList,
  property,
  updateProperty,
  whenData,
  alreadyAddedProperties
}: Props) => {
  const [automationProperty, setAutomationProperty] = useState(property ?? '');
  const {t} = useTranslation();
  useEffect(() => {
    setAutomationProperty(property ?? '');
  }, [property]);

  const removeSpaces = (text: string) => {
    return text?.replace(/\s/g, '');
  };
  propertiesList = propertiesList.filter(x => x.header && x.label);
  
  const getSelectedProperty = (thisProperty) => {
    return propertiesList.find(x => x.key.toLowerCase() == thisProperty.toLowerCase());
  }

  const getPropertyLabel = (thisProperty: string) => {
    if (thisProperty.startsWith(`${CustomPropertiesAnswerJsonKey}.`))
      return getSelectedProperty(thisProperty)?.label ?? '';

    return getTranslateAutomationProperties(removeSpaces(thisProperty)?.toLowerCase(), whenData?.entityName) ?? '';
  }

  const committedLabel = getPropertyLabel(automationProperty);
  const { activeQuery, stopEditing, searchQueryProps } = useDropdownSearchQuery(committedLabel);

  const automationPropertySelect = (_e, p) => {
    const selection = readSelection(p.value, 'key', automationProperty);

    stopEditing();

    // guard clause - re-selecting the property already set would clear the row's operator and value
    if (!selection.isChange) return;

    setAutomationProperty(selection.value);
    updateProperty(selection.value);
  };

  const alreadyAdded = findAlreadyAddedProperty(activeQuery, alreadyAddedProperties?.filter(x => x.header && x.label));
  const noResultsMessage = alreadyAdded
    ? t('automation.property-field.already-added', { property: alreadyAdded.header })
    : t('automation.property-field.we-couldnt-find-any-matches');

  return (
    <ProviderConsumer render={globalTheme =>
      <div className={Styles.GroupInput}>
        <FormLabel content={t('automation.property-field.property')} styles={{ fontSize: '12px' }} />
        <Dropdown
          search
          clearable
          {...searchQueryProps}
          {...(automationProperty && {
            value: getSelectedProperty(automationProperty)
          })}
          noResultsMessage={noResultsMessage}
          inverted
          fluid
          items={propertiesList}
          style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background }}
          placeholder={t('automation.property-field.start-typing-a-property')}
          highlightFirstItemOnOpen
          onChange={automationPropertySelect}
        />
      </div>
    } />
  );
};
